import { subDays } from 'date-fns';

import { useGetMeterIdentifiers } from '@/hooks/usage/useGetMeterIdentifiers';
import { useSmartMeterMeasurements } from '@/hooks/usage/useSmartMeterMeasurements';
import { useDateRangeToggle } from '@/hooks/utils/useDateRangeToggle';
import { formatEndDatesForSmartDataThresholds } from '@/lib/charts';
import {
  MeterCapabilityType,
  ReadingFrequencyType,
} from '@/types/generated/graphql/schema';

const MAX_DAYS_IN_PAST = 30;
const MINIMUM_NUMBER_OF_READINGS = 20;

export const useShouldShowSmartChart = () => {
  const { dateRangeStartAtEndAtMapping } = useDateRangeToggle();

  const {
    data: meterIdentifiers,
    isLoading: isLoadingIdentifiers,
    isError: isErrorIdentifiers,
  } = useGetMeterIdentifiers();

  const {
    meterPointReference: marketSupplyPointId,
    serialNumber: deviceId,
    capabilityType,
  } = meterIdentifiers || {};

  const isSmart = capabilityType === MeterCapabilityType.Ami;
  const isEnabled = !!marketSupplyPointId && !!deviceId && isSmart;

  const formattedEnd = formatEndDatesForSmartDataThresholds(
    dateRangeStartAtEndAtMapping.DAY.endAt
  );

  const {
    data: smartData,
    isLoading: isLoadingSmartData,
    isError: isSmartDataError,
  } = useSmartMeterMeasurements({
    startAt: subDays(
      dateRangeStartAtEndAtMapping.DAY.startAt,
      MAX_DAYS_IN_PAST
    ),
    endAt: formattedEnd.DAY,
    utilityFilters: [
      {
        waterFilters: {
          readingFrequencyType: ReadingFrequencyType.DayInterval,
          marketSupplyPointId,
          deviceId,
        },
      },
    ],
    isEnabled,
    queryKey: 'initialSmartMeterChartEligibility',
  });

  const measurementCount = smartData?.measurements?.length ?? 0;
  const showChartBasedOnMinimumMeasurementCount =
    measurementCount >= MINIMUM_NUMBER_OF_READINGS;

  return {
    showChartBasedOnMinimumMeasurementCount,
    isLoading: isLoadingIdentifiers || isLoadingSmartData,
    isError: isErrorIdentifiers || isSmartDataError,
    showNotEnoughSmartReadsAlert:
      measurementCount < MINIMUM_NUMBER_OF_READINGS && isSmart,
    isSmart,
  };
};
